/* eslint class-methods-use-this: 0 */

import BaseController from './BaseController';
import ProjectDataSource from '../dataSources/ProjectDataSource';
import MetadataDataSource from '../dataSources/MetadataDataSource';
import LibraryGroup from '../objects/LibraryGroup';
import { markLibraryGroupRemoved, saveMapLayers } from '../helpers/mapDefaults';
import { trackLayerChange, visibilityAction } from '../helpers/mapLayerTracking';
import { MAP_LAYER_ACTION } from '../helpers/Mixpanel';

class LayerLibraryController extends BaseController {
    static get name() {
        return 'LayerLibraryController';
    }

    static getInstance(options) {
        return new LayerLibraryController(options);
    }

    onActivate() {
        this.bindGluBusEvents({
            UPDATE_LAYER_LIBRARY_LAYER_VISIBILITY: this.onUpdateLibraryLayerVisibility,
            UPDATE_LAYER_LIBRARY_GROUP_VISIBILITY: this.onUpdateLibraryGroupVisibility,
            UPDATE_USER_LIBRARY_LAYER_GROUP_VISIBILITY: this.onUpdateUserLibraryLayerGroupVisibility,
            ADD_LAYER_LIBRARY_GROUP: this.onAddLibraryGroup,
            REMOVE_LAYER_LIBRARY_GROUP: this.onRemoveLibraryGroup,
        });

        this.projectDataSource = this.activateSource(ProjectDataSource);
        this.metadataDataSource = this.activateSource(MetadataDataSource);
    }

    get viewCode() {
        return this.projectDataSource.currentProject.viewCode;
    }

    applyLibraryDataLayers(mapInstance) {
        saveMapLayers(this.bus, mapInstance);
        this.bus.emit('MAP_APPLY_LIBRARY_DATA_LAYERS_UPDATE', { mapInstanceId: mapInstance.id });
    }

    /**
     * @param {object} e
     * @param {string} e.mapInstanceId
     * @param {boolean} e.visible
     */
    onUpdateUserLibraryLayerGroupVisibility(e) {
        const { mapInstanceId, visible } = e;
        const mapInstance = this.projectDataSource.getActiveMapInstance(mapInstanceId);

        mapInstance.userDataLayers.forEach(layer => {
            layer.visible = visible;
            this.bus.emit('MAP_APPLY_USER_LAYERS_UPDATE', {
                layer,
                mapInstanceId,
            });
        });
    }

    /**
     * @param {object} e
     * @param {string} e.id
     * @param {string} e.groupId
     * @param {string} e.mapInstanceId
     */
    onUpdateLibraryLayerVisibility(e) {
        const { id, groupId, mapInstanceId } = e;

        const mapInstance = this.projectDataSource.getActiveMapInstance(mapInstanceId);
        const libraryGroup = mapInstance.libraryDataLayers.find(olg => olg.id === groupId);

        const { layers } = libraryGroup;
        const libraryLayer = layers.find(l => l.id === id);
        if (!libraryLayer) return;
        libraryLayer.visible = !libraryLayer.visible;

        this.applyLibraryDataLayers(mapInstance);
        trackLayerChange(
            this.viewCode,
            visibilityAction(libraryLayer.visible),
            libraryGroup,
            libraryLayer,
        );
    }

    /**
     * @param {object} e
     * @param {string} e.id
     * @param {boolean} e.visible
     * @param {string} e.mapInstanceId
     */
    onUpdateLibraryGroupVisibility(e) {
        const { id, visible, mapInstanceId } = e;

        const mapInstance = this.projectDataSource.getActiveMapInstance(mapInstanceId);
        const libraryGroup = mapInstance.libraryDataLayers.find(olg => olg.id === id);

        if (!libraryGroup) return;

        const { layers } = libraryGroup;
        libraryGroup.visible = visible;
        layers.forEach(layer => {
            layer.visible = visible;
        });

        this.applyLibraryDataLayers(mapInstance);
        trackLayerChange(this.viewCode, visibilityAction(visible), libraryGroup);
    }

    /**
     * @param {object} e
     * @param {object} e.group
     * @param {string} e.mapInstanceId
     */
    onAddLibraryGroup(e) {
        const { group, mapInstanceId } = e;
        const { id } = group;

        const mapInstance = this.projectDataSource.getActiveMapInstance(mapInstanceId);

        if (mapInstance.libraryDataLayers.some(libraryDataLayer => libraryDataLayer.id === id)) {
            return;
        }

        const groupDefinition = this.metadataDataSource.libraryData.groups.find(g => g.id === id);
        const libraryGroup = LibraryGroup.fromDefinition(groupDefinition);

        if (!libraryGroup) return;

        mapInstance.libraryDataLayers.push(libraryGroup);

        markLibraryGroupRemoved(this.bus, id, false);
        this.applyLibraryDataLayers(mapInstance);
        trackLayerChange(this.viewCode, MAP_LAYER_ACTION.ADD, libraryGroup);
    }

    /**
     * @param {object} e
     * @param {string} e.id
     * @param {string} e.mapInstanceId
     */
    onRemoveLibraryGroup(e) {
        const { id, mapInstanceId } = e;

        const mapInstance = this.projectDataSource.getActiveMapInstance(mapInstanceId);
        const libraryGroupIndex = mapInstance.libraryDataLayers.findIndex(olg => olg.id === id);
        if (libraryGroupIndex === -1) return;
        const [libraryGroup] = mapInstance.libraryDataLayers.splice(libraryGroupIndex, 1);

        markLibraryGroupRemoved(this.bus, id, true);
        this.applyLibraryDataLayers(mapInstance);
        trackLayerChange(this.viewCode, MAP_LAYER_ACTION.REMOVE, libraryGroup);
    }

    onDeactivate() {
        this.unbindGluBusEvents();
    }
}

export default LayerLibraryController;
